import { useQuery } from '@tanstack/react-query';
import { toApiError, useApi, type ApiError, type ArrivalStepPreviewResponse } from '@/shared/api';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { toUpsertRequest, type ArrivalStepDraft } from './arrivalDraft';
import type { ArrivalScope } from './useArrivalGuideApi';

/**
 * Rendert den Entwurf serverseitig — mit derselben Auflösung, die der Gast bekommt. Eine
 * Vorschau, die im Browser nachbaut, wie der Server ersetzt, ist genau so lange richtig, bis
 * eine der beiden Seiten sich ändert.
 *
 * Der Türcode bleibt auch hier maskiert: er gehört einer Buchung, nicht dem Apartment.
 */
export function useArrivalPreview(
  scope: ArrivalScope,
  draft: ArrivalStepDraft,
  previewPropertyId: string | null,
) {
  const api = useApi();
  const body = toUpsertRequest(draft);
  const debounced = useDebounce(JSON.stringify(body), 400);

  return useQuery<ArrivalStepPreviewResponse, ApiError>({
    queryKey: ['arrival-steps', 'preview', scope.kind, scope.id, debounced, previewPropertyId],
    queryFn: async () => {
      const request = { ...JSON.parse(debounced), previewPropertyId };
      try {
        return await (scope.kind === 'object'
          ? api.arrivalSteps.previewForObject(scope.id, request)
          : api.arrivalSteps.previewForApartment(scope.id, request));
      } catch (error) {
        throw await toApiError(error);
      }
    },
    enabled: draft.titleDe.trim().length > 0 || draft.bodyDe.trim().length > 0,
    staleTime: 30_000,
  });
}
